import { CardContent } from "@/components/card"
import { Card } from "@/components/card"
import { Link } from "react-router-dom"
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { confirmEmailService } from "@/api/services/auth/authService"
import displayError from "@/api/errors/errorResponse"
import { getErrorResponse } from "@/api/errors/errorCodes"

interface ConfirmEmailProps {
    id: String,
    token: String,
}

export default function ConfirmEmail(props: ConfirmEmailProps) {

  const [status, changeStatus] = useState<"CONFIRMED" | "ERROR" | "LOADING">("LOADING")
  const [info, changeInfo] = useState<String>("")

  const confirmEmail = async () => {
    const response = await confirmEmailService(props.id, props.token)
    if(!response.success) {
      changeInfo(getErrorResponse(response.status) ?? "Nie udało się potwierdzić Twojego adresu email. Spróbuj ponownie później.")
      changeStatus("ERROR")
      displayError(response)
      return
    }
    changeStatus("CONFIRMED")
  }

  useEffect(() => {
    confirmEmail()
  }, [])

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <div className="bg-gradient-to-r from-purple-400 to-purple-500 p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-white text-xl font-semibold mb-1">Potwierdzenie emaila</h1>
              <div className="text-purple-100 text-sm">
                {status === "LOADING" && (
                  <div className="flex items-center gap-2">
                    <span>Trwa potwierdzanie...</span>
                  </div>
                )}
                {status === "ERROR" && "Wystąpił błąd"}
                {status === "CONFIRMED" && "Twoje konto zostało aktywowane."}
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="w-20 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                <div className="w-12 h-10 bg-white/30 rounded"></div>
              </div>
            </div>
          </div>
        </div>

        <CardContent className="p-6">
          {status === "LOADING" && (
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
              </div>
              <p className="text-gray-600">Trwa potwierdzanie Twojego adresu email...</p>
            </div>
          )}

          {status === "ERROR" && (
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Wystąpił błąd
              </h2>
              <p className="text-gray-600 mb-6">
                {info}
              </p>
              <Link
                to="/login"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Wróć do logowania
              </Link>
            </div>
          )}

          {status === "CONFIRMED" && (
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Email potwierdzony!
              </h2>
              <p className="text-gray-600 mb-6">
                Twoje konto zostało pomyślnie aktywowane. Możesz się teraz zalogować.
              </p>
              <Link
                to="/login"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Przejdź do logowania
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}